import { useAppStore } from '@core/store'
import SpatialTree from './index'
import LogicalBrowser from './LogicalBrowser'
import EntitiesBrowser from './EntitiesBrowser'
import TypesBrowser from './types/TypesBrowser'

// Three-tab navigator for the IFC indexes that live in the left
// sidebar. Tab labels mirror IFC schema vocabulary so users that know
// the schema recognise immediately what each surface contains:
//
//   Estrutura espacial → IfcSpatialStructureElement hierarchy
//                         (IfcProject → IfcSite → IfcBuilding → ...)
//   Sistemas e grupos  → IfcSystem / IfcGroup / IfcZone /
//                         IfcElementAssembly / IfcGrid / IfcSpatialZone
//   Entidades          → flat list of every IFC entity present
//                         (the EXPRESS-level construct: IfcWall, ...)
const TABS: Array<{
  id: 'spatial' | 'logical' | 'entities' | 'types'
  label: string
  tooltip: string
}> = [
  {
    id: 'spatial',
    label: 'Estrutura espacial',
    tooltip:
      'Hierarquia IfcSpatialStructureElement do IFC: Project → Site → Building → Storey → Space → elementos contidos.',
  },
  {
    id: 'logical',
    label: 'Sistemas e grupos',
    tooltip:
      'Agregações lógicas via IfcRelAssignsToGroup: IfcSystem, IfcDistributionSystem, IfcBuildingSystem, IfcGroup, IfcZone, IfcElementAssembly, IfcGrid, IfcSpatialZone.',
  },
  {
    id: 'entities',
    label: 'Entidades',
    tooltip:
      'Lista plana de todas as entidades IFC presentes no modelo (IfcWall, IfcSlab, IfcRelAssignsToGroup, …), agrupadas por família do schema EXPRESS.',
  },
  {
    id: 'types',
    label: 'Types',
    tooltip:
      'Os IfcXxxType do modelo (IfcRelDefinesByType): as instâncias de cada um, os atributos e os property sets do type, e os elementos que não têm type.',
  },
]

function SidebarTabs() {
  const tab = useAppStore((s) => s.leftSidebarTab)
  const setTab = useAppStore((s) => s.setLeftSidebarTab)
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div
        role="tablist"
        className="flex shrink-0 border-b border-[var(--color-border)] bg-[var(--color-panel)]"
      >
        {TABS.map((t) => {
          const active = t.id === tab
          return (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setTab(t.id)}
              title={t.tooltip}
              className={`flex-1 px-2 py-1.5 text-[10px] uppercase tracking-wide ${
                active
                  ? 'border-b-2 border-[var(--color-accent)] text-[var(--color-text)]'
                  : 'border-b-2 border-transparent text-[var(--color-text-muted)] hover:text-[var(--color-text)]'
              }`}
            >
              {t.label}
            </button>
          )
        })}
      </div>
      <div className="min-h-0 flex-1 overflow-hidden">
        {tab === 'spatial' && (
          <div className="h-full overflow-y-auto overflow-x-hidden">
            <SpatialTree />
          </div>
        )}
        {tab === 'logical' && (
          <div className="h-full overflow-y-auto overflow-x-hidden">
            <LogicalBrowser />
          </div>
        )}
        {tab === 'entities' && <EntitiesBrowser />}
        {tab === 'types' && <TypesBrowser />}
      </div>
    </div>
  )
}

export default SidebarTabs
